import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rows } from "@/lib/api";
import type { Address } from "@/lib/types";
import { Button, Card, EmptyState, Field, PageHeader, Skeleton } from "@/components/ui";
import { AddressForm, EMPTY_ADDRESS } from "@/components/AddressForm";

export function Addresses() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Address | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api.get<{ data: Address[] }>("/v1/addresses"),
  });

  const create = useMutation({
    mutationFn: (address: Address) => api.post<Address>("/v1/addresses", address),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
      setDraft(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/addresses/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["addresses"] }),
  });

  return (
    <>
      <PageHeader
        title="Address book"
        subtitle="Saved pickup and delivery points, ready to drop into a booking."
        action={
          !draft && <Button onClick={() => setDraft({ ...EMPTY_ADDRESS })}>Add address</Button>
        }
      />

      {draft && (
        <Card className="mb-5">
          <h2 className="mb-4 text-lg font-semibold tracking-tight">New address</h2>
          <Field
            label="Label"
            className="mb-4 sm:max-w-xs"
            value={draft.label ?? ""}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder="Bakery, Warehouse, Home"
          />
          <AddressForm prefix="collection" value={draft} onChange={setDraft} />
          <div className="mt-5 flex gap-2">
            <Button loading={create.isPending} onClick={() => create.mutate(draft)}>
              Save address
            </Button>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : !rows(data).length ? (
        !draft && (
          <EmptyState
            title="No saved addresses"
            body="Save the places you ship from and to, and booking drops to a few clicks."
            action={<Button onClick={() => setDraft({ ...EMPTY_ADDRESS })}>Add address</Button>}
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows(data).map((address) => (
            <Card key={address.id}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium">{address.label || address.suburb}</p>
                {address.is_default && <span className="text-xs text-ink-400">Default</span>}
              </div>
              <p className="mt-2 text-sm text-ink-600">
                {address.street}
                <br />
                {address.suburb}, {address.city}
                <br />
                {address.province} {address.postal_code}
              </p>
              <p className="mt-2 text-sm text-ink-400">
                {address.contact_name}, {address.phone}
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="mt-3 px-0"
                onClick={() => address.id && remove.mutate(address.id)}
              >
                Remove
              </Button>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
